import { forwardRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import {
  CapitalAmount,
  setBalanceDialog,
  setCapitalAmountTransactionsDialog,
  setCapitalAmountTransactionsSearchBody,
} from "../../../../app/slices/capitalAmountSlice";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DateRangepicker from "../../../../helpers/dateRangePicker";
import { GiPayMoney, GiReceiveMoney } from "react-icons/gi";
import Permission from "../../../../components/userPermission/userPermission";
import CapitalAmountBalanceForm from "./balance";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CapitalAmountTransactions = () => {
  const dispatch = useDispatch();

  const _Transactions = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions
  );
  const _SearchBody = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions.searchBody
  );
  const loading = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions.loading
  );
  const _BalanceDialog = useSelector(
    (state) => state.capitalAmount.balance.dialog
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [page, setPage] = useState(15);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleDialog = () => {
    dispatch(setCapitalAmountTransactionsDialog(null));
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "amount",
      headerName: "المبلغ",
      flex: 1,
      renderCell: (params) =>
        (params.row.amount ?? 0).toLocaleString
          ? Number(params.row.amount).toLocaleString()
          : params.row.amount,
    },
    {
      field: "transfer_type",
      headerName: "النوع",
      flex: 1,
      renderCell: (params) =>
        params.row.transfer_type?.name ??
        params.row.transfer_type_name ??
        (params.row.transfer_type_id === 1 ? "إيداع" : "سحب"),
    },
    {
      field: "by_user",
      headerName: "بواسطة",
      flex: 1,
      renderCell: (params) =>
        params.row.by_user?.name ?? params.row.by_user ?? "",
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) =>
        params.row.created_at ? (
          <Stack>
            <Typography variant="body2">
              {dayjs(params.row.created_at).format("YYYY-MM-DD")}
            </Typography>
            <Typography variant="caption">
              {dayjs(params.row.created_at).format("hh:mm a")}
            </Typography>
          </Stack>
        ) : (
          ""
        ),
    },
  ];

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي :{" "}
          {(_Transactions.total_amount ?? 0).toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  useEffect(() => {
    if (!_Transactions.capital_amount_id) return;
    const timeout = setTimeout(() => {
      dispatch(CapitalAmount.getAllCapitalAmountTransactions(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch, _Transactions.capital_amount_id]);

  useEffect(() => {
    const body = {
      capital_amount_id: _Transactions.capital_amount_id,
      skip: currentPage * page,
      take: page,
    };
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
    dispatch(setCapitalAmountTransactionsSearchBody(body));
  }, [
    _Transactions.capital_amount_id,
    currentPage,
    dispatch,
    page,
    startDate,
    endDate,
  ]);

  return (
    <Dialog
      open={_Transactions.dialog}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      dir="rtl"
      onClose={() => {
        handleDialog();
      }}
      aria-describedby="alert-dialog-slide-description"
      maxWidth="lg"
    >
      <DialogContent>
        <Typography
          mb={3}
          variant="h4"
          color={"purple"}
          gutterBottom
          component="div"
        >
          {"المعاملات"}
        </Typography>
        {_BalanceDialog && <CapitalAmountBalanceForm />}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <DateRangepicker
              startDate={startDate}
              endDate={endDate}
              handleOnChange={(searchDate) => {
                const from = searchDate[0].startDate
                  ? dayjs(searchDate[0].startDate).format("YYYY-MM-DD")
                  : null;
                const to = searchDate[0].endDate
                  ? dayjs(searchDate[0].endDate).format("YYYY-MM-DD")
                  : null;
                if (from !== startDate || to !== endDate) {
                  setStartDate(from);
                  setEndDate(to);
                  setCurrentPage(0);
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Stack direction="row" spacing={1}>
              <Permission permission="capital_amount_edit">
                <Tooltip title="اضافة رصيد">
                  <IconButton
                    color="info"
                    onClick={() =>
                      dispatch(
                        setBalanceDialog({
                          id: _Transactions.capital_amount_id,
                          type: "deposit",
                        })
                      )
                    }
                  >
                    <GiReceiveMoney />
                  </IconButton>
                </Tooltip>
              </Permission>
              <Permission permission="capital_amount_edit">
                <Tooltip title="سحب رصيد">
                  <IconButton
                    color="error"
                    onClick={() =>
                      dispatch(
                        setBalanceDialog({
                          id: _Transactions.capital_amount_id,
                          type: "withdraw",
                        })
                      )
                    }
                  >
                    <GiPayMoney />
                  </IconButton>
                </Tooltip>
              </Permission>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: "70vh", width: "100%" }}>
              <DataGrid
                rows={
                  _Transactions.data?.map((x, index) => ({
                    ...x,
                    index: index + 1,
                  })) ?? []
                }
                getRowId={(row) => row.id ?? row.number ?? row.index}
                columns={columns}
                pageSize={page}
                page={currentPage}
                rowCount={_Transactions.total}
                loading={loading}
                rowHeight={50}
                pagination
                paginationMode="server"
                selectionModel={[]}
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                }}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => {
                  setPage(newPageSize);
                }}
                components={{
                  LoadingOverlay: LinearProgress,
                  Footer: CustomFooter,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CapitalAmountTransactions;
